import { useState, useEffect } from 'react';

import * as apiComms from '../apiComms';
import * as logger from '../logger';
import IRELAND_COUNTY_PATHS from '../data/irelandCountyPaths';

const PROG = 'ChartCustomersByCountyMap.js';

const COUNTY_ALIASES = {
  'co antrim': 'antrim',
  'county antrim': 'antrim',
  'co armagh': 'armagh',
  'county armagh': 'armagh',
  'co carlow': 'carlow',
  'county carlow': 'carlow',
  'co cavan': 'cavan',
  'county cavan': 'cavan',
  'co clare': 'clare',
  'county clare': 'clare',
  'co cork': 'cork',
  'county cork': 'cork',
  'co derry': 'derry',
  'county derry': 'derry',
  'londonderry': 'derry',
  'co donegal': 'donegal',
  'county donegal': 'donegal',
  'co down': 'down',
  'county down': 'down',
  'co dublin': 'dublin',
  'county dublin': 'dublin',
  'dublin city': 'dublin',
  'co fermanagh': 'fermanagh',
  'county fermanagh': 'fermanagh',
  'co galway': 'galway',
  'county galway': 'galway',
  'co kerry': 'kerry',
  'county kerry': 'kerry',
  'co kildare': 'kildare',
  'county kildare': 'kildare',
  'co kilkenny': 'kilkenny',
  'county kilkenny': 'kilkenny',
  'co laois': 'laois',
  'county laois': 'laois',
  'co leitrim': 'leitrim',
  'county leitrim': 'leitrim',
  'co limerick': 'limerick',
  'county limerick': 'limerick',
  'co longford': 'longford',
  'county longford': 'longford',
  'co louth': 'louth',
  'county louth': 'louth',
  'co mayo': 'mayo',
  'county mayo': 'mayo',
  'co meath': 'meath',
  'county meath': 'meath',
  'co monaghan': 'monaghan',
  'county monaghan': 'monaghan',
  'co offaly': 'offaly',
  'county offaly': 'offaly',
  'co roscommon': 'roscommon',
  'county roscommon': 'roscommon',
  'co sligo': 'sligo',
  'county sligo': 'sligo',
  'co tipperary': 'tipperary',
  'county tipperary': 'tipperary',
  'co tyrone': 'tyrone',
  'county tyrone': 'tyrone',
  'co waterford': 'waterford',
  'county waterford': 'waterford',
  'co westmeath': 'westmeath',
  'county westmeath': 'westmeath',
  'co wexford': 'wexford',
  'county wexford': 'wexford',
  'co wicklow': 'wicklow',
  'county wicklow': 'wicklow'
};

const ChartCustomersByCountyMap = (props) => {

  const [countyData, setCountyData] = useState([]);
  const [totalMetricWithCounty, setTotalMetricWithCounty] = useState(0);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const metricType = props.metricType || 'customers';
  const isSpendMetric = metricType === 'spend';
  const hasProvidedCountyData = Array.isArray(props.countyData);

  useEffect(() => {
    if (hasProvidedCountyData) {
      setIsLoadingData(false);
      return;
    }

    const loadChartData = async () => {
      const FUNCTION = 'loadChartData';

      try {
        setIsLoadingData(true);
        const _chartDataResponse = isSpendMetric ?
          await apiComms.getChartDataTotalSpendByCounty(props.timeRange) :
          await apiComms.getChartDataCustomersByCounty();
        if (_chartDataResponse && !_chartDataResponse.isErr) {
          setCountyData(_chartDataResponse.response.chartdata || []);
          setTotalMetricWithCounty(
            isSpendMetric ?
              (_chartDataResponse.response.totalSpendWithCounty || 0) :
              (_chartDataResponse.response.totalCustomersWithCounty || 0)
          );
        }
        setIsLoadingData(false);
      }
      catch (e) {
        logger.error('Exception Occured', { PROG, FUNCTION });
        console.log('Exception: '+e+', '+e.stack);
      }
    };

    loadChartData();
  }, [hasProvidedCountyData, isSpendMetric, props.timeRange]);

  const normaliseCounty = (county) => {
    const normalised = String(county ?? '')
      .trim()
      .toLowerCase()
      .replace(/\./g, '')
      .replace(/\s+/g, ' ');

    return COUNTY_ALIASES[normalised] || normalised.replace(/^county /, '').replace(/^co /, '');
  };

  const effectiveCountyData = hasProvidedCountyData ? props.countyData : countyData;
  const effectiveTotalMetricWithCounty = hasProvidedCountyData ? Number(props.totalMetricWithCounty || 0) : totalMetricWithCounty;

  const valuesByCounty = effectiveCountyData.reduce((acc, countyRec) => {
    const countyKey = normaliseCounty(countyRec.county);
    if (countyKey !== '') {
      const countyValue = isSpendMetric ? countyRec.totalSpend : countyRec.customerCount;
      acc[countyKey] = (acc[countyKey] || 0) + Number(countyValue || 0);
    }
    return acc;
  }, {});

  const maxValue = Math.max(1, ...Object.values(valuesByCounty));
  const mappedValue = IRELAND_COUNTY_PATHS.reduce((total, countyPath) => total + (valuesByCounty[countyPath.key] || 0), 0);
  const unmappedValue = Math.max(0, effectiveTotalMetricWithCounty - mappedValue);
  const topCounties = [...IRELAND_COUNTY_PATHS]
    .map((countyPath) => ({
      ...countyPath,
      metricValue : valuesByCounty[countyPath.key] || 0
    }))
    .sort((a, b) => b.metricValue - a.metricValue)
    .slice(0, 8);

  const formatMetric = (value) => {
    if (isSpendMetric) {
      return `€${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits : 0 })}`;
    }

    return Number(value || 0).toLocaleString();
  };

  const formatMapMetric = (value) => {
    if (!isSpendMetric) {
      return Number(value || 0).toLocaleString();
    }

    const numericValue = Number(value || 0);
    if (numericValue >= 1000000) {
      return `${(numericValue / 1000000).toFixed(1)}m`;
    }
    if (numericValue >= 1000) {
      return `${Math.round(numericValue / 1000)}k`;
    }
    return Math.round(numericValue).toString();
  };

  const getTitle = () => {
    if (props.title) {
      return props.title;
    }

    return isSpendMetric ? 'Total Spend By County' : 'Customers By County';
  };

  const getCardTitle = () => {
    if (props.cardTitle) {
      return props.cardTitle;
    }

    return isSpendMetric ? 'Ireland Spend Heat Map By County' : 'Ireland Customer Heat Map By County';
  };

  const getTotalPillText = () => {
    if (props.totalPillLabel) {
      return `${formatMetric(effectiveTotalMetricWithCounty)} ${props.totalPillLabel}`;
    }

    return isSpendMetric ? `${formatMetric(effectiveTotalMetricWithCounty)} spend with county` : `${formatMetric(effectiveTotalMetricWithCounty)} customers with county`;
  };

  const getUnmappedText = () => {
    return isSpendMetric ?
      `${formatMetric(unmappedValue)} spend was linked to county values not matched to an Ireland county.` :
      `${formatMetric(unmappedValue)} customer county values were not matched to an Ireland county.`;
  };

  const getCountyColour = (value) => {
    if (value <= 0) {
      return '#d8edf4';
    }

    const intensity = value / maxValue;
    if (intensity > 0.75) {
      return '#006d77';
    }
    if (intensity > 0.5) {
      return '#118ab2';
    }
    if (intensity > 0.25) {
      return '#40b8c4';
    }
    return '#91d7df';
  };

  return (
    <div className='card'>
      <div style={ styles.shell }>
        <div style={ styles.headerRow }>
          <div>
            <div style={ styles.eyebrow }>{ props.eyebrow || 'Customer Geography' }</div>
            <div style={ styles.title }>{ getTitle() }</div>
          </div>
          <div style={ styles.totalPill }>{ getTotalPillText() }</div>
        </div>

        <div style={ styles.contentGrid }>
          <div style={ styles.mapPanel }>
            {!isLoadingData ? (
              <svg viewBox="0 0 100 110" role="img" aria-label="Ireland customer heat map by county" style={ styles.mapSvg }>
                <g>
                {IRELAND_COUNTY_PATHS.map((countyPath) => {
                  const value = valuesByCounty[countyPath.key] || 0;
                  return (
                    <g key={ countyPath.key }>
                      <path
                        d={ countyPath.path }
                        fill={ getCountyColour(value) }
                        stroke="#ffffff"
                        strokeWidth="0.55"
                        vectorEffect="non-scaling-stroke"
                      >
                        <title>{ `${countyPath.label}: ${formatMetric(value)}` }</title>
                      </path>
                      <text x={ countyPath.centroid[0] } y={ countyPath.centroid[1] + 0.9 } textAnchor="middle" style={ styles.mapLabel }>
                        { value > 0 ? formatMapMetric(value) : countyPath.label.slice(0, 2).toUpperCase() }
                      </text>
                    </g>
                  );
                })}
                </g>
              </svg>
            ) : null}
          </div>

          <div style={ styles.rankingPanel }>
            <div style={ styles.rankingTitle }>Top Counties</div>
            {topCounties.map((countyPoint) => (
              <div key={ countyPoint.key } style={ styles.countyRow }>
                <div style={ styles.countyName }>{ countyPoint.label }</div>
                <div style={ styles.barTrack }>
                  <div style={{ ...styles.barFill, width : `${maxValue > 0 ? (countyPoint.metricValue / maxValue) * 100 : 0}%` }}></div>
                </div>
                <div style={ styles.countyCount }>{ formatMetric(countyPoint.metricValue) }</div>
              </div>
            ))}
            { unmappedValue > 0 ? (
              <div style={ styles.unmappedNote }>
                { getUnmappedText() }
              </div>
            ) : null }
          </div>
        </div>
      </div>
      <div className='card-body'>
        { getCardTitle() }
      </div>
    </div>
  );
};

const styles = {
  shell : {
    padding : 20,
    background : 'linear-gradient(135deg, #f7fbfc 0%, #edf8f4 100%)'
  },
  headerRow : {
    display : 'flex',
    justifyContent : 'space-between',
    alignItems : 'flex-start',
    gap : 12,
    marginBottom : 14,
    flexWrap : 'wrap'
  },
  eyebrow : {
    fontSize : 12,
    textTransform : 'uppercase',
    letterSpacing : 1.4,
    color : '#087f8c',
    fontWeight : 800
  },
  title : {
    fontSize : 22,
    fontWeight : 800,
    color : '#153243'
  },
  totalPill : {
    padding : '8px 12px',
    borderRadius : 999,
    backgroundColor : 'white',
    color : '#087f8c',
    fontWeight : 800,
    border : '1px solid #cfe9ec'
  },
  contentGrid : {
    display : 'grid',
    gridTemplateColumns : 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
    gap : 18,
    alignItems : 'center'
  },
  mapPanel : {
    minHeight : 430,
    display : 'flex',
    alignItems : 'center',
    justifyContent : 'center',
    background : 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.95), rgba(231,247,249,0.5))',
    borderRadius : 16,
    border : '1px solid #d7eef1'
  },
  mapSvg : {
    width : '100%',
    maxWidth : 520,
    height : 430
  },
  mapLabel : {
    fontSize : 3.2,
    fontWeight : 800,
    fill : '#073b4c',
    pointerEvents : 'none'
  },
  rankingPanel : {
    backgroundColor : 'rgba(255,255,255,0.86)',
    border : '1px solid #d7eef1',
    borderRadius : 16,
    padding : 16
  },
  rankingTitle : {
    fontSize : 16,
    fontWeight : 800,
    color : '#153243',
    marginBottom : 12
  },
  countyRow : {
    display : 'grid',
    gridTemplateColumns : '86px 1fr 78px',
    gap : 10,
    alignItems : 'center',
    marginBottom : 10
  },
  countyName : {
    fontSize : 13,
    fontWeight : 700,
    color : '#264653'
  },
  barTrack : {
    height : 9,
    borderRadius : 999,
    backgroundColor : '#e7f3f5',
    overflow : 'hidden'
  },
  barFill : {
    height : '100%',
    borderRadius : 999,
    background : 'linear-gradient(90deg, #7fd8be, #087f8c)'
  },
  countyCount : {
    fontSize : 13,
    fontWeight : 800,
    color : '#087f8c',
    textAlign : 'right'
  },
  unmappedNote : {
    marginTop : 12,
    fontSize : 12,
    color : '#6c757d'
  }
};

export default ChartCustomersByCountyMap;
